import { useEffect, useState, useCallback, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/StatCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { RefreshCw, Loader2, Landmark, Zap } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const day = (d) => new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });

const Stat = ({ label, value, id, note }) => (
  <Card className="p-5" data-testid={`stripe-stat-${id}`}>
    <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
    <div className="mt-1 text-2xl font-semibold text-slate-900">{usd(value)}</div>
    {note && <div className="mt-1 text-xs text-slate-500">{note}</div>}
  </Card>
);

const Arrivals = ({ rows }) => (
  <Card className="p-6" data-testid="stripe-arrivals">
    <h3 className="mb-3 font-semibold text-slate-900">Incoming payments</h3>
    {rows.length === 0 ? <p className="text-sm text-slate-400">No pending payments right now.</p> : rows.map((r) => (
      <div key={r.date} className="flex justify-between border-b border-slate-100 py-2 text-sm last:border-0" data-testid={`stripe-arrival-${r.date}`}>
        <span>{day(r.date)} <span className="text-xs text-slate-500">· {r.count} payment{r.count === 1 ? "" : "s"}</span></span><b>{usd(r.amount)}</b>
      </div>))}
  </Card>
);

const Payouts = ({ rows }) => (
  <Card className="p-6" data-testid="stripe-payouts">
    <h3 className="mb-3 font-semibold text-slate-900">Recent cash-outs</h3>
    {rows.length === 0 ? <p className="text-sm text-slate-400">No cash-outs yet.</p> : rows.map((p) => (
      <div key={p.id} className="flex flex-wrap justify-between gap-2 border-b border-slate-100 py-2 text-sm last:border-0" data-testid={`stripe-payout-${p.id}`}>
        <span>{usd(p.amount)} · {p.method === "instant" ? "Instant" : "ACH"} <Badge className="ml-1 border-0 bg-slate-100 capitalize text-slate-700">{p.status.replace("_", " ")}</Badge></span>
        <span className="text-xs text-slate-500">arrives {day(p.arrival_date)}</span>
      </div>))}
  </Card>
);

const CashOut = ({ data, onDone }) => {
  const [method, setMethod] = useState("standard");
  const [amount, setAmount] = useState("");
  const [dest, setDest] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const idem = useRef("");
  const instantOk = data.destinations !== null && data.destinations.some((d) => d.instant) && data.balance.instant_available > 0;
  const instant = method === "instant" && instantOk;
  const room = instant ? data.balance.instant_available : data.balance.available;
  const listed = data.destinations !== null;
  const dests = (data.destinations || []).filter((d) => (instant ? d.instant : d.type === "bank account"));
  const fee = instant ? Math.max(0.5, Math.round(Number(amount || 0) * 1.5) / 100) : 0;
  useEffect(() => { setDest(dests.find((d) => d.default)?.id || dests[0]?.id || ""); }, [method]); // eslint-disable-line react-hooks/exhaustive-deps
  const open = () => { idem.current = `payout-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`; setConfirm(true); };
  const go = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data: p } = await api.post("/admin/stripe/payout", { amount: Number(amount), method: instant ? "instant" : "standard", destination: dest, idempotency_key: idem.current });
      setLast({ ok: true, text: `Payout ${p.id}: ${usd(p.amount)} - status ${p.status.replace("_", " ")}, expected ${day(p.arrival_date)}` });
      toast.success(`Cash-out of ${usd(p.amount)} submitted`);
      setAmount(""); onDone();
    } catch (e) { setLast({ ok: false, text: apiError(e) }); toast.error(apiError(e)); }
    setBusy(false); setConfirm(false);
  };
  const opt = (m, Icon, title, sub) => (
    <button onClick={() => setMethod(m)} data-testid={`stripe-method-${m}`}
      className={`flex flex-1 items-start gap-2 rounded-lg border p-3 text-left transition-colors ${method === m ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:bg-slate-50"}`}>
      <Icon className="mt-0.5 h-4 w-4" /><span><b className="block text-sm">{title}</b><span className="text-xs text-slate-500">{sub}</span></span>
    </button>);
  const valid = Number(amount) > 0 && Number(amount) <= room && (dest || !listed);
  const destLabel = listed ? dests.find((d) => d.id === dest)?.label : "your linked Stripe bank account";
  return (
    <Card className="p-6" data-testid="stripe-cashout">
      <h3 className="mb-3 font-semibold text-slate-900">Cash out</h3>
      <div className="mb-3 flex flex-col gap-2 sm:flex-row">
        {opt("standard", Landmark, "Standard payout to bank", "No Stripe fee · usually 1-2 business days")}
        {instantOk && opt("instant", Zap, "Instant to debit card", "Stripe confirmed eligible · fee shown before you confirm")}
      </div>
      {listed && dests.length === 0 ? <p className="text-sm text-amber-700" data-testid="stripe-no-destination">
        {instant ? "No debit card that supports instant transfers is on your Stripe account. Add one in your Stripe dashboard under Settings, Payouts." : "No bank account is on your Stripe account yet. Add one in your Stripe dashboard under Settings, Payouts."}</p> : (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          {listed ? <select value={dest} onChange={(e) => setDest(e.target.value)} className="h-10 rounded-md border border-slate-200 px-3 text-sm" data-testid="stripe-destination">
            {dests.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
          </select> : <div className="text-sm text-slate-600 sm:pb-2" data-testid="stripe-default-destination">To {destLabel}</div>}
          <div className="flex-1"><div className="mb-1 text-xs text-slate-500">Amount (up to {usd(room)})</div>
            <Input type="number" min="0.01" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="stripe-amount" /></div>
          <Button variant="outline" onClick={() => setAmount(String(room))} data-testid="stripe-amount-max">Max</Button>
          <Button disabled={!valid || busy} onClick={open} className="bg-slate-900 hover:bg-slate-800" data-testid="stripe-cashout-btn">Cash out</Button>
        </div>)}
      {!instantOk && <p className="mt-2 text-xs text-slate-500" data-testid="stripe-instant-unavailable">Instant payouts appear here only when Stripe confirms an instant-eligible debit card and instant balance.</p>}
      {last && <p className={`mt-3 text-sm ${last.ok ? "text-emerald-700" : "text-red-700"}`} data-testid="stripe-payout-result">{last.text}</p>}
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="stripe-confirm-dialog">
          <AlertDialogHeader><AlertDialogTitle>Cash out {usd(amount)}?</AlertDialogTitle>
            <AlertDialogDescription>{instant ? "Instant payout" : "Standard payout"} to {destLabel}.{instant && ` Estimated Stripe instant fee: ${usd(fee)} (Stripe's standard 1.5%, $0.50 minimum - Stripe deducts the final fee).`}{data.mode !== "live" && " This Stripe key is in test mode, so no real money moves."}</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel disabled={busy} data-testid="stripe-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); go(); }} disabled={busy} data-testid="stripe-confirm-ok">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Yes, cash out"}</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default function AdminStripe() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => api.get("/admin/stripe/overview").then((r) => { setData(r.data); setErr(""); }).catch((e) => setErr(apiError(e))), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-6" data-testid="admin-stripe-page">
      <PageHeader title="Stripe Payments" subtitle="Your Stripe balance, incoming payments and cash-outs. Ava calls you when new payments are ready to cash out." />
      <div className="flex items-center gap-2">
        {data && data.mode !== "live" && <Badge className="border-0 bg-amber-100 text-amber-800" data-testid="stripe-test-mode">Test-mode key - balances shown are Stripe test data</Badge>}
        <Button variant="outline" size="sm" onClick={load} data-testid="stripe-refresh"><RefreshCw className="mr-1 h-4 w-4" />Refresh</Button>
      </div>
      {err && <Card className="p-4 text-sm text-red-700" data-testid="stripe-error">{err}</Card>}
      {!data && !err && <Loader2 className="h-5 w-5 animate-spin text-slate-400" />}
      {data && <>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <Stat id="available" label="Available to cash out" value={data.balance.available} />
          <Stat id="instant" label="Available for instant" value={data.balance.instant_available} />
          <Stat id="pending" label="Incoming (pending)" value={data.balance.pending} />
          <Stat id="after" label="Balance after incoming" value={data.balance.available + data.balance.pending} note="Once pending payments arrive" />
          <Stat id="ytd" label={`Earnings ${new Date().getFullYear()} to date`} value={data.ytd} note="Net of Stripe fees" />
        </div>
        <CashOut data={data} onDone={load} />
        <div className="grid gap-6 lg:grid-cols-2"><Arrivals rows={data.arrivals} /><Payouts rows={data.payouts} /></div>
      </>}
    </div>
  );
}
